import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LocalizedText } from '@cr/shared';
import { api } from '@/lib/api';
import { localized } from '@/lib/format';
import Icon, { isIconName } from '@/components/icon';
import { EmptyState, ErrorState, Skeleton, SubHeader } from '@/components/ui';

export interface CategoryNode {
  id: string;
  name: LocalizedText;
  imageUrl: string | null;
  icon: string | null;
  level: number;
  /** Товары во всей подветке — ровно то, что покажет каталог по этой категории */
  productsCount: number;
  children: CategoryNode[];
}

function useCategoryTree() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<CategoryNode[]>('/catalog/categories')).data,
  });
}

/** Дерево приходит целиком, поэтому нужную ветку ищем на клиенте без лишнего запроса. */
function findNode(nodes: CategoryNode[], id: string): CategoryNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children, id);
    if (found) return found;
  }
  return null;
}

/** У листа подкатегорий нет — сразу открываем каталог с фильтром. */
function targetOf(category: CategoryNode): string {
  return category.children.length > 0
    ? `/categories/${category.id}`
    : `/catalog?categoryId=${category.id}`;
}

export default function CategoriesPage() {
  const { t } = useTranslation();
  const tree = useCategoryTree();

  if (tree.isLoading) {
    return (
      <div className="px-4 sm:px-6">
        <CategoriesSkeleton />
      </div>
    );
  }
  if (tree.isError) return <ErrorState onRetry={() => void tree.refetch()} />;

  const roots = tree.data ?? [];
  if (roots.length === 0) return <EmptyState title={t('home.empty')} />;

  // Первые две ветки показываем крупными плитками — они задают тон экрану
  const [first, second, ...rest] = roots;

  return (
    <div className="px-4 pb-8 pt-1 sm:px-6">
      <h1 className="mb-4 font-display text-[26px] font-semibold">{t('home.categories')}</h1>

      <div className="grid grid-cols-2 gap-3">
        {first && <FeaturedTile category={first} tone="bordo" />}
        {second && <FeaturedTile category={second} tone="gold" />}
      </div>

      {rest.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {rest.map((category) => (
            <CategoryTile key={category.id} category={category} />
          ))}
        </div>
      )}
    </div>
  );
}

export function CategoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const tree = useCategoryTree();

  if (tree.isLoading) {
    return (
      <div>
        <SubHeader title={t('common.loading')} />
        <div className="px-4 sm:px-6">
          <CategoriesSkeleton />
        </div>
      </div>
    );
  }

  const category = id ? findNode(tree.data ?? [], id) : null;

  if (!category) {
    return (
      <div>
        <SubHeader title={t('home.categories')} />
        <div className="px-4 sm:px-6">
          <EmptyState title={t('catalog.nothingFound')} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <SubHeader title={localized(category.name)} />
      <div className="px-4 pb-8 sm:px-6">
        <Link
          to={`/catalog?categoryId=${category.id}`}
          className="mb-4 flex items-center justify-between gap-2 rounded-card bg-bordo px-4 py-3.5 text-white active:bg-bordo-dark"
        >
          <span className="text-[15px] font-semibold">{t('catalog.showAllInCategory')}</span>
          <Icon name="chevron" size={18} />
        </Link>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {category.children.map((child) => (
            <CategoryTile key={child.id} category={child} />
          ))}
        </div>
      </div>
    </div>
  );
}

function FeaturedTile({ category, tone }: { category: CategoryNode; tone: 'bordo' | 'gold' }) {
  const { t } = useTranslation();

  return (
    <Link
      to={targetOf(category)}
      className={`flex min-h-[150px] flex-col justify-between rounded-card p-5 text-white active:opacity-90 ${
        tone === 'gold' ? 'bg-gold' : 'bg-bordo'
      }`}
      style={
        category.imageUrl
          ? {
              backgroundImage: `linear-gradient(rgb(28 26 25 / 0.35), rgb(28 26 25 / 0.6)), url(${category.imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : undefined
      }
    >
      <Icon
        name={isIconName(category.icon) ? category.icon : tone === 'gold' ? 'crown' : 'sparkles'}
        size={30}
      />
      <div className="mt-8">
        <p className="font-display text-2xl font-semibold leading-tight">
          {localized(category.name)}
        </p>
        {category.productsCount > 0 && (
          <p className="text-sm text-white/85">
            {t('catalog.itemsCount', { count: category.productsCount })}
          </p>
        )}
      </div>
    </Link>
  );
}

function CategoryTile({ category }: { category: CategoryNode }) {
  const { t } = useTranslation();

  return (
    <Link
      to={targetOf(category)}
      className="flex flex-col items-center gap-2 rounded-card bg-surface p-4 text-center shadow-soft active:scale-[0.98]"
    >
      <span className="grid size-14 place-items-center overflow-hidden rounded-full bg-cream">
        {category.imageUrl ? (
          <img src={category.imageUrl} alt="" loading="lazy" className="size-full object-cover" />
        ) : (
          <Icon
            name={isIconName(category.icon) ? category.icon : 'shirt'}
            size={26}
            className="text-bordo"
          />
        )}
      </span>
      <span className="font-display text-base leading-tight">{localized(category.name)}</span>
      {category.productsCount > 0 && (
        <span className="text-xs text-muted">
          {t('catalog.itemsCount', { count: category.productsCount })}
        </span>
      )}
    </Link>
  );
}

function CategoriesSkeleton() {
  return (
    <div className="pt-1">
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-[150px] rounded-card" />
        <Skeleton className="h-[150px] rounded-card" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[132px] rounded-card" />
        ))}
      </div>
    </div>
  );
}
